import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { FormShell, SubmitBar, SubmitError } from "./form-utils";
import { FieldWrap, inputCls, NameFields, PhoneInput, EmailInput, Select } from "./fields";
import { getTracking } from "@/lib/tracking";
import { submitLead, newSubmissionId, normalizeSubmitError } from "@/lib/submit";
import { emailError, phoneError } from "@/lib/format";

const REASONS = ["Sell a property", "Investor inquiry", "Buyer inquiry", "Strategic partner", "Other"];
const BLANK = { first_name: "", last_name: "", email: "", phone: "", interest: "", message: "" };

export default function ContactForm({ initialMessage = "" }) {
  const [data, setData] = useState({ ...BLANK, message: initialMessage });
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);
  const submissionId = useRef(newSubmissionId());
  const inFlight = useRef(false);
  const successRef = useRef(null);
  const successHeadingRef = useRef(null);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (!done) return;
    // Wait one frame so the on-screen keyboard has closed and the success card has laid out, then bring it under the header
    const id = requestAnimationFrame(() => {
      successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      successHeadingRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [done]);

  const validate = () => {
    const e = {};
    if (!data.first_name.trim()) e.first_name = "Required";
    if (!data.last_name.trim()) e.last_name = "Required";
    if (emailError(data.email)) e.email = emailError(data.email);
    if (data.phone && phoneError(data.phone)) e.phone = phoneError(data.phone);
    if (!data.message.trim()) e.message = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev?.preventDefault?.();
    if (inFlight.current) return;
    if (!validate()) return;
    inFlight.current = true;
    setPending(true);
    setSubmitError(null);
    const tracking = getTracking();
    try {
      await submitLead("/api/contact", { ...data, source: tracking.utm_source || "direct", ...tracking }, submissionId.current);
      submissionId.current = newSubmissionId();
      setDone(true);
    } catch (err) {
      const norm = normalizeSubmitError(err);
      if (Object.keys(norm.fields).length) setErrors(norm.fields);
      setSubmitError(norm);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  if (done) return (
    <div ref={successRef} className="card-frame p-7 md:p-10 scroll-mt-24 md:scroll-mt-28" data-testid="contact-form-success-card">
      <div className="mb-8">
        <h3 ref={successHeadingRef} tabIndex={-1} className="font-display text-2xl md:text-3xl text-white outline-none" data-testid="contact-form-success-heading">Thank you.</h3>
      </div>
      <div className="flex items-start gap-3" data-testid="contact-form-success" role="status">
        <CheckCircle2 className="text-[#C19B6C] shrink-0 mt-0.5" size={20} />
        <p className="text-neutral-200 leading-relaxed">Thank you for your submission. Someone from Brightside Capital Ventures will contact you shortly to respond to your inquiry.</p>
      </div>
    </div>
  );

  return (
    <FormShell title="General Inquiry" subtitle="Tell us how we can help.">
      <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="contact-form">
        <NameFields first={data.first_name} last={data.last_name} onChange={set} errors={errors} testIdPrefix="contact" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FieldWrap label="Email" error={errors.email}><EmailInput value={data.email} onChange={(v) => set("email", v)} testId="contact-email" /></FieldWrap>
          <FieldWrap label="Phone" optional error={errors.phone}><PhoneInput value={data.phone} onChange={(v) => set("phone", v)} testId="contact-phone" /></FieldWrap>
        </div>
        <FieldWrap label="Reason for contact" optional><Select options={REASONS} value={data.interest} onChange={(v) => set("interest", v)} testId="contact-interest" /></FieldWrap>
        <FieldWrap label="Message" error={errors.message}>
          <textarea rows={5} className={inputCls} value={data.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
        </FieldWrap>
        <SubmitError error={submitError} pending={pending} onRetry={onSubmit} />
        <SubmitBar pending={pending} label="Send Message" />
      </form>
    </FormShell>
  );
}
